/**
 * Funnel Attribution Section (MA-IMPL-117)
 * 
 * Campaign assignment and attribution configuration for funnels.
 * 
 * Features:
 * - Campaign selector (assign/change campaign)
 * - Effective attribution display (inherited vs overridden)
 * - Override controls for each attribution field
 * - Visual indicators showing inheritance state
 * 
 * Key principle: Attribution is resolved at funnel SAVE time, not at lead creation.
 * This component shows the resolved values and allows overrides.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Building2, 
  Tags, 
  Signal, 
  Layers,
  Link2,
  Unlink2,
  ChevronDown,
  CheckCircle2,
  Info,
  Loader2,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
  SelectSeparator,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';

// =============================================================================
// Campaign Selector Component
// =============================================================================
function CampaignSelector({ 
  selectedCampaignId, 
  onCampaignChange, 
  campaigns = [],
  isLoading = false,
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-slate-300 font-medium">Assigned Campaign</Label>
        {selectedCampaignId && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onCampaignChange("")}
            className="h-6 px-2 text-xs text-slate-500 hover:text-slate-300"
          >
            <Unlink2 className="h-3 w-3 mr-1" />
            Unlink
          </Button>
        )}
      </div>
      
      <Select
        value={selectedCampaignId || "__none__"}
        onValueChange={(value) => onCampaignChange(value === "__none__" ? "" : value)}
        disabled={isLoading}
      >
        <SelectTrigger 
          className="bg-white/5 border-white/10 text-slate-100 h-10"
          data-testid="campaign-selector"
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading...</span>
            </div>
          ) : (
            <SelectValue placeholder="Select a campaign (optional)" />
          )}
        </SelectTrigger>
        <SelectContent className="bg-slate-900/95 backdrop-blur-lg border-white/10">
          <SelectGroup>
            <SelectLabel className="text-slate-400 text-xs px-2">No Campaign</SelectLabel>
            <SelectItem value="__none__" className="text-slate-300">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-slate-500" />
                <span>No campaign assigned</span>
              </div>
            </SelectItem>
          </SelectGroup>
          
          {campaigns.length > 0 && (
            <>
              <SelectSeparator className="bg-white/10" />
              <SelectGroup>
                <SelectLabel className="text-slate-400 text-xs px-2">Available Campaigns</SelectLabel>
                {campaigns.map((campaign) => (
                  <SelectItem 
                    key={campaign.id} 
                    value={campaign.id} 
                    className="text-slate-200"
                  >
                    <div className="flex items-center gap-2">
                      <div className={cn(
                        "h-2 w-2 rounded-full",
                        campaign.status === 'active' ? "bg-emerald-400" : "bg-amber-400"
                      )} />
                      <span>{campaign.name}</span>
                      {campaign.funnel_count > 0 && (
                        <span className="text-xs text-slate-500 ml-auto">
                          {campaign.funnel_count} funnel{campaign.funnel_count !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectGroup>
            </>
          )}
        </SelectContent>
      </Select>
      
      {!selectedCampaignId && (
        <p className="text-xs text-slate-500">
          Assign this funnel to a campaign to inherit default attribution values.
        </p>
      )}
    </div>
  );
}

// =============================================================================
// Attribution Field Row Component
// =============================================================================
function AttributionFieldRow({
  label,
  icon: Icon,
  value,
  displayValue,
  campaignValue,
  campaignDisplayValue,
  isOverridden,
  onOverrideToggle,
  hasCampaign,
  options = [],
  onValueChange,
  placeholder,
}) {
  // Determine effective value source
  const effectiveValue = isOverridden ? value : (hasCampaign ? campaignValue : value);
  const effectiveDisplayValue = isOverridden ? displayValue : (hasCampaign ? campaignDisplayValue : displayValue);
  const source = isOverridden ? 'funnel' : (hasCampaign && campaignValue ? 'campaign' : 'funnel');
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-slate-400" />
          <Label className="text-xs text-slate-300">{label}</Label>
        </div>
        
        {hasCampaign && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2">
                  <Badge 
                    variant="outline" 
                    className={cn(
                      "text-[10px] h-5 px-1.5",
                      source === 'campaign' 
                        ? "border-indigo-500/30 text-indigo-300 bg-indigo-500/10"
                        : "border-emerald-500/30 text-emerald-300 bg-emerald-500/10"
                    )}
                  >
                    {source === 'campaign' ? (
                      <>
                        <Link2 className="h-2.5 w-2.5 mr-1" />
                        Inherited
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-2.5 w-2.5 mr-1" />
                        Override
                      </>
                    )}
                  </Badge>
                  <Switch
                    checked={isOverridden}
                    onCheckedChange={onOverrideToggle}
                    className="scale-75"
                    data-testid={`override-toggle-${label.toLowerCase().replace(/\s/g, '-')}`}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="left" className="bg-slate-800 border-slate-700">
                <p className="text-xs">
                  {isOverridden 
                    ? "Override: Using funnel-specific value" 
                    : "Inherited: Using campaign value"
                  }
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      
      <div className="flex items-center gap-2">
        <Select
          value={value || "__not_set__"}
          onValueChange={(val) => onValueChange(val === "__not_set__" ? "" : val)}
          disabled={hasCampaign && !isOverridden}
        >
          <SelectTrigger 
            className={cn(
              "bg-white/5 border-white/10 text-slate-100 h-9 flex-1",
              hasCampaign && !isOverridden && "opacity-60"
            )}
          >
            <SelectValue placeholder={placeholder || `Select ${label.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent className="bg-slate-900/95 backdrop-blur-lg border-white/10">
            <SelectItem value="__not_set__" className="text-slate-400">
              Not set
            </SelectItem>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value} className="text-slate-200">
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Show campaign value when inheriting */}
      {hasCampaign && !isOverridden && campaignDisplayValue && (
        <p className="text-xs text-indigo-400/80 flex items-center gap-1">
          <Link2 className="h-3 w-3" />
          From campaign: {campaignDisplayValue}
        </p>
      )}
    </div>
  );
}

// =============================================================================
// Main Attribution Section Component
// =============================================================================
export default function FunnelAttributionSection({ 
  funnel, 
  onFunnelUpdate,
  isLoading: externalLoading = false,
}) {
  const [campaigns, setCampaigns] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  
  // Get current funnel values
  const funnelId = funnel?.id;
  const campaignId = funnel?.campaign_id;
  const vertical = funnel?.vertical;
  const leadSourceId = funnel?.lead_source_id;
  const channel = funnel?.channel;
  const attributionOverrides = funnel?.attribution_overrides || {};
  
  // Fetch campaigns and lead sources
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [campaignsRes, leadSourcesRes] = await Promise.all([
        api.get('/marketing-campaigns/selector/list'),
        api.get('/lead-sources'),
      ]);
      setCampaigns(campaignsRes.data || []);
      setLeadSources(leadSourcesRes.data || []);
      
      // Find selected campaign details
      if (campaignId) {
        const campaign = campaignsRes.data?.find(c => c.id === campaignId);
        setSelectedCampaign(campaign || null);
      } else {
        setSelectedCampaign(null);
      }
    } catch (err) {
      console.error('[FunnelAttributionSection] Error fetching data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [campaignId]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Handle campaign change
  const handleCampaignChange = (newCampaignId) => {
    const campaign = campaigns.find(c => c.id === newCampaignId);
    setSelectedCampaign(campaign || null);
    
    onFunnelUpdate({
      campaign_id: newCampaignId || null,
    });
  };
  
  // Handle attribution field change
  const handleAttributionChange = (field, value) => {
    onFunnelUpdate({
      [field]: value || null,
    });
  };
  
  // Handle override toggle
  const handleOverrideToggle = (field, isOverridden) => {
    const currentOverrides = funnel?.attribution_overrides || {};
    const newOverrides = {
      ...currentOverrides,
      [field]: isOverridden,
    };
    
    onFunnelUpdate({
      attribution_overrides: newOverrides,
    });
  };
  
  // Channel options
  const channelOptions = [
    { value: 'paid', label: 'Paid' },
    { value: 'organic', label: 'Organic' },
    { value: 'referral', label: 'Referral' },
    { value: 'direct', label: 'Direct' },
  ];
  
  // Vertical options (common verticals)
  const verticalOptions = [
    { value: 'medicare', label: 'Medicare' },
    { value: 'mortgage', label: 'Mortgage' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'solar', label: 'Solar' },
    { value: 'home_services', label: 'Home Services' },
    { value: 'legal', label: 'Legal' },
    { value: 'financial', label: 'Financial' },
    { value: 'education', label: 'Education' },
    { value: 'other', label: 'Other' },
  ];
  
  // Lead source options from API
  const leadSourceOptions = leadSources.map(ls => ({
    value: ls.id,
    label: ls.name,
  }));
  
  // Get campaign values for inheritance
  const campaignVertical = selectedCampaign?.vertical;
  const campaignLeadSourceId = selectedCampaign?.lead_source_id;
  const campaignChannel = selectedCampaign?.channel;
  
  // Get display names
  const getLeadSourceName = (id) => {
    const ls = leadSources.find(l => l.id === id);
    return ls?.name || id;
  };
  
  const getVerticalName = (value) => {
    const v = verticalOptions.find(o => o.value === value);
    return v?.label || value;
  };
  
  const getChannelName = (value) => {
    const c = channelOptions.find(o => o.value === value);
    return c?.label || value;
  };
  
  const hasCampaign = !!campaignId && !!selectedCampaign;
  
  return (
    <div className="space-y-6" data-testid="attribution-section">
      {/* Section Header */}
      <div>
        <div className="flex items-center gap-2">
          <Tags className="h-5 w-5 text-indigo-400" />
          <h2 className="text-xl font-bold text-slate-100">Attribution</h2>
        </div>
        <p className="text-slate-200/60 text-sm mt-1">
          Configure campaign assignment and attribution for leads created from this funnel
        </p>
      </div>
      
      {/* Campaign Assignment Card */}
      <Card className="glass border-white/10">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-slate-100 text-base">
            <Building2 className="h-5 w-5 text-blue-400" />
            Campaign Assignment
          </CardTitle>
          <CardDescription className="text-slate-400 text-sm">
            Link this funnel to a campaign to inherit default attribution values
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <CampaignSelector
            selectedCampaignId={campaignId}
            onCampaignChange={handleCampaignChange}
            campaigns={campaigns}
            isLoading={isLoading}
          />
          
          {/* Campaign Details Preview */}
          {hasCampaign && (
            <div className="p-3 rounded-lg bg-indigo-500/5 border border-indigo-500/20">
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-lg bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                  <Building2 className="h-4 w-4 text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-200">{selectedCampaign.name}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                    <span className="flex items-center gap-1">
                      <div className={cn(
                        "h-1.5 w-1.5 rounded-full",
                        selectedCampaign.status === 'active' ? "bg-emerald-400" : "bg-amber-400"
                      )} />
                      {selectedCampaign.status}
                    </span>
                    {selectedCampaign.funnel_count !== undefined && (
                      <span>{selectedCampaign.funnel_count} linked funnel{selectedCampaign.funnel_count !== 1 ? 's' : ''}</span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Campaign Attribution Preview */}
              <div className="mt-3 pt-3 border-t border-indigo-500/20 grid grid-cols-3 gap-2 text-xs">
                <div>
                  <span className="text-slate-500">Vertical:</span>
                  <span className="ml-1 text-slate-300">{campaignVertical ? getVerticalName(campaignVertical) : '—'}</span>
                </div>
                <div>
                  <span className="text-slate-500">Source:</span>
                  <span className="ml-1 text-slate-300">{campaignLeadSourceId ? getLeadSourceName(campaignLeadSourceId) : '—'}</span>
                </div>
                <div>
                  <span className="text-slate-500">Channel:</span>
                  <span className="ml-1 text-slate-300">{campaignChannel ? getChannelName(campaignChannel) : '—'}</span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Attribution Fields Card */}
      <Card className="glass border-white/10">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-slate-100 text-base">
            <Layers className="h-5 w-5 text-emerald-400" />
            Attribution Fields
          </CardTitle>
          <CardDescription className="text-slate-400 text-sm">
            {hasCampaign 
              ? "Values are inherited from campaign. Toggle override to set funnel-specific values."
              : "Set attribution values for leads created from this funnel."
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Vertical */}
          <AttributionFieldRow
            label="Vertical"
            icon={Building2}
            value={vertical}
            displayValue={vertical ? getVerticalName(vertical) : null}
            campaignValue={campaignVertical}
            campaignDisplayValue={campaignVertical ? getVerticalName(campaignVertical) : null}
            isOverridden={attributionOverrides.vertical || false}
            onOverrideToggle={(checked) => handleOverrideToggle('vertical', checked)}
            hasCampaign={hasCampaign}
            options={verticalOptions}
            onValueChange={(value) => handleAttributionChange('vertical', value)}
            placeholder="Select vertical"
          />
          
          {/* Lead Source */}
          <AttributionFieldRow
            label="Lead Source"
            icon={Signal}
            value={leadSourceId}
            displayValue={leadSourceId ? getLeadSourceName(leadSourceId) : null}
            campaignValue={campaignLeadSourceId}
            campaignDisplayValue={campaignLeadSourceId ? getLeadSourceName(campaignLeadSourceId) : null}
            isOverridden={attributionOverrides.lead_source_id || false}
            onOverrideToggle={(checked) => handleOverrideToggle('lead_source_id', checked)}
            hasCampaign={hasCampaign}
            options={leadSourceOptions}
            onValueChange={(value) => handleAttributionChange('lead_source_id', value)}
            placeholder="Select lead source"
          />
          
          {/* Channel */}
          <AttributionFieldRow
            label="Channel"
            icon={Tags}
            value={channel}
            displayValue={channel ? getChannelName(channel) : null}
            campaignValue={campaignChannel}
            campaignDisplayValue={campaignChannel ? getChannelName(campaignChannel) : null}
            isOverridden={attributionOverrides.channel || false}
            onOverrideToggle={(checked) => handleOverrideToggle('channel', checked)}
            hasCampaign={hasCampaign}
            options={channelOptions}
            onValueChange={(value) => handleAttributionChange('channel', value)}
            placeholder="Select channel"
          />
        </CardContent>
      </Card>
      
      {/* Info Note */}
      <Card className="border-white/5 bg-white/[0.01]">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="h-4 w-4 text-slate-500 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-slate-500 space-y-1">
              <p>
                <strong className="text-slate-400">How attribution works:</strong> When a lead is created, 
                the effective attribution values are snapshotted to the lead record.
              </p>
              <p>
                {hasCampaign 
                  ? "Fields marked as 'Inherited' will use the campaign's values. Toggle 'Override' to set funnel-specific values."
                  : "Without a campaign, the funnel's direct attribution values will be used."
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
